// Le dossier adapté, EN PLACE dans la fiche offre — plus une page à part.
//
// Jusqu'au 25 août 2026, générer un dossier ouvrait `#/offre/:id/dossier/:did`,
// un écran qu'on voyait une fois puis qu'il fallait rouvrir depuis une liste de
// « dossiers générés » pour y revenir. Nicolas : « je n'aime pas que ça ouvre
// une nouvelle page qu'on ne revoit plus ». Ce composant remplace cette page :
// `Offre.tsx` le monte directement dans sa colonne de droite dès que le
// candidat sélectionné a un dossier, et le démonte pour le CTA de génération
// sinon. Un candidat, un dossier courant, jamais une liste de pages.
//
// UNE seule surface d'édition : celle de l'extension (`PreviewModal` de
// `packages/ui`), rendue ICI (`embedded`) plutôt que par-dessus. Le panneau de
// réglages et l'historique sont DÉJÀ des tiroirs repliables À L'INTÉRIEUR de
// `PreviewModal` (son propre `showSettings`) : rien à refaire ici pour la
// demande « réglages en popover », le composant partagé le fait déjà.
//
// L'HISTORIQUE DES VERSIONS EST INTERNE À CE COMPOSANT, pas remonté au
// parent : « régénérer » crée une nouvelle ligne en base (principe « on garde
// tout », jamais un UPDATE), et c'est ce composant qui décide quelle version il
// affiche, via son propre état local `dossierIdActuel`. Le parent ne connaît
// que le dossier le plus RÉCENT pour ce candidat (`dossierId`, prop) : au
// premier montage on part de là, ensuite l'utilisateur peut naviguer dans
// l'historique ou régénérer sans jamais en informer le parent — remonter
// chaque bascule de version reproduirait exactement la navigation de page
// qu'on vient de retirer, en silence cette fois.

import { useEffect, useMemo, useRef, useState } from 'react'
import { LetterAnglesModal, PreviewModal, phraseChangements, printFrame, type LivePreviewSettings, type ModeleCatalogue } from '@ai-job-fit/ui'
import { langueDuCv } from '@ai-job-fit/engine/outputLanguage'
import type { LetterAngle, LetterAngleQuestion } from '@ai-job-fit/engine/letterAngles'
import { chiffresNonSourcés } from '@ai-job-fit/engine/chiffres'
import { ACCENT_NOIR, ACCENT_PALETTE, renderResumeToHTML, type ResumeTemplate } from '@ai-job-fit/engine/resumeHtml'
import { renderDiffHtml } from '@ai-job-fit/engine/resumeDiffHtml'
import { renderCoverLetterToHTML } from '@ai-job-fit/engine/coverLetterHtml'
import { harvestResume } from '@ai-job-fit/engine/resumeHarvest'
import { get, post, type CandidatureStatus, type GapQuestion } from '../lib/api'
import { fullDate } from '../lib/format'
import { useLabels } from '../lib/labels'
import { useT } from '../lib/i18n'
import { useData, Loading, Empty, useToast } from './common'
import { useFormule } from '../lib/formule'
import { SelecteurEtape } from './candidature'
import { QuestionsEcart } from './gapQuestions'

interface DossierData {
  id: number
  offer_id: number
  consultant_id: number
  adapted: any
  diff: any
  letter: any | null
  settings: {
    language: string
    optimization: string
    tone: string
    length: string
    sections?: Record<string, boolean>
    letterHeader?: 'none' | 'sender' | 'name'
    template?: ResumeTemplate
    accentColor?: string
  }
  created_at: string
  /** Lettre reprise d'une version précédente du CV (issue #179). */
  letterStale: boolean
  letterVersions: { at: string }[]
  letterIndex: number
}

/** Où en est « Ma mise en page » (Radar, issue #235), `GET /api/mise-en-page`. */
interface EtatMiseEnPage {
  etat: 'aucune' | 'en_cours' | 'prete' | 'echec'
  /** Le modèle, seulement si la formule l'ouvre. */
  modele: string | null
  utilisable: boolean
}

/** Les couleurs de Radar : le noir en tête, puis la palette partagée. */
const PALETTE_RADAR = [ACCENT_NOIR, ...ACCENT_PALETTE]

/**
 * Le rendu d'un dossier tel que l'aperçu l'affiche. En Radar, ATS n'est plus
 * proposé (issue #235) : « Sobre » en noir en tient la place, et c'est ce que
 * montrent les dossiers d'avant le catalogue, tous forcés à ATS.
 */
function renduInitial(s: DossierData['settings'], radar: boolean): LivePreviewSettings {
  const base: LivePreviewSettings = { sections: s.sections, letterHeader: s.letterHeader, template: s.template, accentColor: s.accentColor }
  if (!radar) return base
  const ats = !s.template || s.template === 'ATS-Optimized'
  return { ...base, template: ats ? 'Moderne' : s.template, accentColor: ats ? ACCENT_NOIR.hex : (s.accentColor ?? ACCENT_NOIR.hex) }
}

export function DossierAtelier({
  offerId,
  dossierId,
  company,
  jobDescription,
  candidature,
  onChanged,
  demandeLettre = 0,
  onLettreEcrite,
  enFenetre = false,
  ouvert = false,
  surLettre = false,
  onOuvrir,
  onFermer,
}: {
  offerId: number
  /** Le dossier le plus récent pour ce candidat, connu du parent. Point de
   *  départ seulement : cf. note de tête sur qui possède quoi. */
  dossierId: number
  company?: string
  /** Le texte de l'offre : une lettre peut en reprendre un chiffre (issue #160). */
  jobDescription?: string
  candidature?: { id: number; status: CandidatureStatus } | null
  onChanged: () => void
  /** Change quand le parcours demande la lettre : l'onglet Lettre s'ouvre. */
  demandeLettre?: number
  /** Une lettre vient d'être écrite ou ajustée. */
  onLettreEcrite?: () => void
  /** Radar : le document s'ouvre en FENÊTRE, comme dans l'extension, et la
   *  fiche ne garde qu'une carte « Votre CV ». Encastré dans la fiche, il
   *  n'avait que 430 px de haut sur un écran de 900, et ses réglages 250 px.
   *  La console garde l'atelier encastré dans sa colonne de droite. */
  enFenetre?: boolean
  /** La fenêtre est ouverte (piloté par la fiche). */
  ouvert?: boolean
  /** La fenêtre s'ouvre sur la lettre (« Rédiger la lettre » du parcours). */
  surLettre?: boolean
  onOuvrir?: () => void
  onFermer?: () => void
}) {
  const tLabels = useLabels()
  const t = useT()
  const radar = useFormule() === 'radar'
  const [dossierIdActuel, setDossierIdActuel] = useState(dossierId)
  useEffect(() => { setDossierIdActuel(dossierId) }, [dossierId])

  const dossier = useData(() => get<DossierData>(`/api/dossiers/${dossierIdActuel}`), [dossierIdActuel])
  const historique = useData(
    () => get<{ id: number; created_at: string }[]>(`/api/dossiers/${dossierIdActuel}/historique`),
    [dossierIdActuel]
  )
  const [regenerating, setRegenerating] = useState(false)
  const [generatingLetter, setGeneratingLetter] = useState(false)
  const [rendu, setRendu] = useState<LivePreviewSettings>({})
  const toast = useToast()
  const vignette = useRef<HTMLIFrameElement>(null)
  // La fenêtre ouverte, la fiche derrière ne défile plus : une seule chose
  // défile à la fois, c'était tout le défaut de l'atelier encastré.
  useEffect(() => {
    if (!enFenetre || !ouvert) return
    const avant = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = avant }
  }, [enFenetre, ouvert])

  const d = dossier.data

  // Questions d'écart à tout moment (issue #166) : le bouton « Questions » de
  // l'aperçu, et avant chaque lettre si le réglage le demande. Les précisions
  // déjà données sont lues sur le profil, pour montrer ce que l'IA prend en
  // compte.
  const profil = useData(
    () => (d ? get<{ profile: any }>(`/api/consultants/${d.consultant_id}`) : Promise.resolve(null)),
    [d?.consultant_id]
  )
  const reglages = useData(() => get<{ questions?: string }>('/api/settings'), [])
  const faits: { topic: string; detail: string }[] = profil.data?.profile?.meta?.enrichmentFacts ?? []
  // Chiffres du CV et de la lettre absents du profil maître, de ses précisions
  // et (pour la lettre) de l'offre : signalés, jamais bloquants (issue #160).
  // Sans le profil chargé, rien n'est signalé plutôt que tout.
  const chiffres = useMemo(() => {
    const maitre = profil.data?.profile
    if (!d || !maitre) return undefined
    return {
      cv: chiffresNonSourcés(d.adapted, [maitre, faits]),
      letter: d.letter ? chiffresNonSourcés(d.letter, [maitre, faits, jobDescription ?? '']) : [],
    }
  }, [d?.adapted, d?.letter, profil.data, jobDescription])
  const [questionsEnCours, setQuestionsEnCours] = useState<{ questions: GapQuestion[]; ensuite: () => Promise<void> } | null>(null)
  // Questions d'angle avant la lettre (issue #165), avec les réglages de
  // rédaction à appliquer ensuite.
  const [anglesEnCours, setAnglesEnCours] = useState<{ questions: LetterAngleQuestion[]; partial?: LivePreviewSettings } | null>(null)

  // Le modèle et la couleur se relisent dans le dossier : ils étaient forcés
  // à ATS, et un choix fait dans l'aperçu disparaissait en rouvrant la fiche.
  useEffect(() => {
    if (d) setRendu(renduInitial(d.settings, radar))
  }, [d?.id])

  // « Ma mise en page » (Radar) : relue tant qu'elle se prépare.
  const miseEnPage = useData(
    () => (radar ? get<EtatMiseEnPage>('/api/mise-en-page') : Promise.resolve(null)),
    [radar]
  )
  const enPreparation = miseEnPage.data?.etat === 'en_cours'
  useEffect(() => {
    if (!enPreparation) return
    const id = setInterval(() => miseEnPage.reload(), 5000)
    return () => clearInterval(id)
  }, [enPreparation, miseEnPage.reload])
  const modelePerso = miseEnPage.data?.modele ?? undefined

  const settings = useMemo(
    () => ({ template: 'ATS-Optimized' as ResumeTemplate, outputLanguage: d?.settings.language, ...rendu, miseEnPage: modelePerso }),
    [d?.settings.language, rendu, modelePerso]
  )
  // « Ma mise en page » ne se retouche pas dans l'aperçu : le modèle de l'IA
  // ne porte pas les marques que la récolte relit.
  const perso = settings.template === 'Perso' && Boolean(modelePerso)

  const html = useMemo(
    () => (d ? renderResumeToHTML(d.adapted, { ...settings, editable: !perso, company }) : ''),
    [d?.id, d?.adapted, settings, company, perso]
  )

  // Le catalogue de Radar (issue #235) : chaque vignette est le vrai CV,
  // rendu avec ce modèle. « Ma mise en page » passe en tête.
  const modeles = useMemo((): ModeleCatalogue[] | undefined => {
    if (!radar || !d) return undefined
    const rendu = (template: ResumeTemplate) => renderResumeToHTML(d.adapted, { ...settings, template, editable: false, company })
    const liste: ModeleCatalogue[] = [
      { id: 'Moderne', label: t('modeles.sobre'), html: rendu('Moderne') },
      { id: 'Dates', label: t('modeles.dates'), html: rendu('Dates') },
      { id: 'Colonnes', label: t('modeles.colonnes'), html: rendu('Colonnes'), note: t('modeles.colonnes.note') },
      { id: 'Compact', label: t('modeles.compact'), html: rendu('Compact') },
    ]
    const mp = miseEnPage.data
    if (mp) {
      const moi: ModeleCatalogue = { id: 'Perso', label: t('modeles.perso'), html: '' }
      if (!mp.utilisable) moi.verrou = t('modeles.perso.complet')
      else if (mp.modele) Object.assign(moi, { html: rendu('Perso'), note: t('modeles.perso.note') })
      else if (mp.etat === 'en_cours') moi.attente = t('modeles.perso.enCours')
      else moi.verrou = t('modeles.perso.aPreparer')
      liste.unshift(moi)
    }
    return liste
  }, [radar, d?.id, d?.adapted, settings, company, miseEnPage.data, t])
  const diffHtml = useMemo(
    () => (d ? renderDiffHtml(d.adapted, d.diff, settings) : ''),
    [d?.id, d?.adapted, d?.diff, settings]
  )
  const letterHtml = useMemo(
    () => (d?.letter ? renderCoverLetterToHTML(d.letter, { ...settings, editable: true }) : undefined),
    [d?.id, d?.letter, settings]
  )

  const availableSections = useMemo(
    () =>
      d
        ? {
            summary: !!d.adapted?.basics?.summary,
            skills: (d.adapted?.skills?.length ?? 0) > 0,
            education: (d.adapted?.education?.length ?? 0) > 0,
            languages: (d.adapted?.languages?.length ?? 0) > 0,
            projects: (d.adapted?.projects?.length ?? 0) > 0,
          }
        : undefined,
    [d]
  )

  const wireRevert = useMemo(
    () => (doc: Document, kind: 'cv' | 'letter' | 'diff') => {
      if (kind !== 'diff') return
      doc.querySelectorAll('li[data-change="reworded"]').forEach((li) => {
        if (li.querySelector('.revert-btn')) return
        const btn = doc.createElement('button')
        btn.className = 'revert-btn'
        btn.type = 'button'
        btn.textContent = t('dossier.revert')
        btn.addEventListener('click', async (e) => {
          e.stopPropagation()
          btn.disabled = true
          btn.textContent = '…'
          try {
            await post(`/api/dossiers/${dossierIdActuel}/revert`, {
              workIndex: Number((li as HTMLElement).dataset.work),
              bulletIndex: Number((li as HTMLElement).dataset.bullet),
            })
            toast(t('dossier.revertedToast'))
            dossier.reload()
          } catch (err: any) {
            toast(err.message, 'err')
            btn.disabled = false
            btn.textContent = t('dossier.revert')
          }
        })
        li.appendChild(btn)
      })
    },
    // `t` dans les dépendances : sans lui, une bascule de langue laisserait
    // ce bouton (posé sur du HTML brut, pas du JSX) figé sur son ancien
    // texte jusqu'au prochain rendu du diff.
    [dossierIdActuel, dossier.reload, toast, t]
  )

  // Les données d'avant restent affichées pendant un rechargement : sans ça,
  // passer à une nouvelle version démontait la fenêtre le temps de la lire.
  if (dossier.loading && !d) return <Loading label={t('dossier.loadingFile')} />
  if (dossier.error || !d) return <Empty>{dossier.error ?? t('dossier.notFound')}</Empty>

  // Une traduction se reconnaît à la langue des deux CV, pas aux compteurs.
  // Le diff de Radar apparie les phrases traduites (crossLingualDiff) : ses
  // chiffres restent valables, la phrase de changements le dit simplement.
  const langueMaitre = langueDuCv(profil.data?.profile)
  const langueDoc = langueDuCv(d.adapted)
  const traduction =
    langueMaitre && langueDoc && langueMaitre !== langueDoc
      ? { to: tLabels(`preview.changes.lang.${langueDoc}`), paired: true }
      : undefined
  const versions = historique.data ?? []
  const cvIndex = versions.findIndex((v) => v.id === dossierIdActuel)
  const cvVersions = versions.map((v) => fullDate(v.created_at))

  if (regenerating && !enFenetre) {
    return (
      <div className="genwait">
        <span className="spinner big" />
        <div className="gw">{t('dossier.regeneratingFile')}</div>
      </div>
    )
  }

  // Régénère avec de nouveaux réglages de rédaction : une ligne de plus en
  // base, jamais une réécriture sur place. La nouvelle version devient celle
  // affichée SANS remonter au parent : cf. note de tête.
  // `instruction` : la consigne libre de la barre d'ajustement, onglet CV.
  const regenererCv = async (partial: LivePreviewSettings, instruction?: string) => {
    setRegenerating(true)
    try {
      const nd = await post<{ id: number }>(`/api/offers/${offerId}/dossier`, {
        consultantId: d.consultant_id,
        instruction,
        settings: {
          optimization: partial.optimization ?? d.settings.optimization,
          tone: partial.tone ?? d.settings.tone,
          length: partial.length ?? d.settings.length,
          // La langue suit le même chemin : oubliée ici jusqu'au 2026-10-11,
          // choisir « Anglais » régénérait en Automatique, donc en français
          // pour une offre en français. Sans choix, on garde celle du dossier.
          language: partial.language ?? d.settings.language,
          // La mise en page affichée suit la nouvelle version.
          template: rendu.template,
          accentColor: rendu.accentColor,
          sections: rendu.sections,
          letterHeader: rendu.letterHeader,
        },
      })
      setDossierIdActuel(nd.id)
      onChanged()
    } catch (e: any) {
      toast(e.message, 'err')
    } finally {
      setRegenerating(false)
    }
  }

  const ajusterLettre = async (instruction: string) => {
    setGeneratingLetter(true)
    try {
      await post(`/api/dossiers/${d.id}/letter/refine`, { instruction })
      dossier.reload()
      onLettreEcrite?.()
    } catch (e: any) {
      toast(e.message, 'err')
    } finally {
      setGeneratingLetter(false)
    }
  }

  const genererLettre = async (partial?: LivePreviewSettings, angles?: LetterAngle[]) => {
    setGeneratingLetter(true)
    try {
      if (partial && (partial.tone || partial.length || partial.optimization || partial.language)) {
        await post(`/api/dossiers/${d.id}/render-settings`, {
          tone: partial.tone, length: partial.length, optimization: partial.optimization, language: partial.language,
        })
      }
      await post(`/api/dossiers/${d.id}/letter`, angles ? { angles } : {})
      dossier.reload()
      onLettreEcrite?.()
    } catch (e: any) {
      toast(e.message, 'err')
    } finally {
      setGeneratingLetter(false)
    }
  }

  // Bouton « Questions » sur le CV : pose les questions d'écart, puis
  // régénère (`ensuite`) une fois répondu ou passé. Rien à demander : on le
  // dit, sans régénérer.
  const avecQuestions = async (ensuite: () => Promise<void>) => {
    try {
      const r = await post<{ questions: GapQuestion[] }>(`/api/offers/${offerId}/gap-questions`, {
        consultantId: d.consultant_id,
      })
      if (r.questions.length > 0) setQuestionsEnCours({ questions: r.questions, ensuite })
      else toast(t('gapQuestions.noneNeeded'))
    } catch (e: any) {
      toast(e.message, 'err')
    }
  }

  const repondre = async (reponses: { topic: string; question: string; detail: string }[]) => {
    const enCours = questionsEnCours
    if (!enCours) return
    setQuestionsEnCours(null)
    const nonVides = reponses.filter((r) => r.detail.trim())
    if (nonVides.length > 0) {
      try {
        await post(`/api/consultants/${d.consultant_id}/enrichment-facts`, { facts: nonVides })
        profil.reload()
      } catch (e: any) {
        toast(e.message, 'err')
      }
    }
    await enCours.ensuite()
  }

  // La lettre passe par les questions d'ANGLE (issue #165), pas par les
  // questions d'écart : avant la première lettre, à chaque fois si le réglage
  // le demande, et sur le bouton « Questions » (`force`). Sans question à
  // poser, la lettre s'écrit directement ; sur le bouton, on le dit.
  const lettreAvecAngle = async (partial?: LivePreviewSettings, force = false) => {
    if (!force && d.letter && reglages.data?.questions !== 'toujours') return genererLettre(partial)
    let questions: LetterAngleQuestion[] = []
    try {
      questions = (await post<{ questions: LetterAngleQuestion[] }>(`/api/dossiers/${d.id}/letter-angles`, {})).questions
    } catch (e: any) {
      if (force) return toast(e.message, 'err')
    }
    if (questions.length > 0) return setAnglesEnCours({ questions, partial })
    if (force) return toast(t('gapQuestions.noneNeeded'))
    return genererLettre(partial)
  }

  return (
    <div className="atelier">
      {anglesEnCours && (
        <LetterAnglesModal
          t={tLabels}
          questions={anglesEnCours.questions}
          onSubmit={async (angles) => {
            const enCours = anglesEnCours
            setAnglesEnCours(null)
            await genererLettre(enCours.partial, angles)
          }}
          onSkip={() => {
            const enCours = anglesEnCours
            setAnglesEnCours(null)
            void genererLettre(enCours.partial)
          }}
          onCancel={() => setAnglesEnCours(null)}
        />
      )}
      {questionsEnCours && (
        <QuestionsEcart
          questions={questionsEnCours.questions}
          onSubmit={repondre}
          onSkip={() => {
            const enCours = questionsEnCours
            setQuestionsEnCours(null)
            void enCours.ensuite()
          }}
        />
      )}
      {enFenetre ? (
        // La carte de la fiche : un aperçu réduit, ce qui a changé, et les deux
        // gestes qui servent sans ouvrir le document.
        <div className="cv-carte" data-testid="cv-carte">
          <button type="button" className="cv-vignette" onClick={onOuvrir} aria-label={t('dossier.ouvrirCv')}>
            <iframe ref={vignette} srcDoc={html} sandbox="allow-same-origin allow-modals" tabIndex={-1} aria-hidden="true" title="" />
          </button>
          <div className="cv-carte-corps">
            <span className="cv-carte-phrase">{phraseChangements(d.diff.stats, d.diff.fidelity, tLabels, traduction)}</span>
            <span className="when">{t('dossier.generatedOn', { date: fullDate(d.created_at) })}</span>
            <span className="cv-carte-actions">
              <button type="button" className="btn-primary btn-sm" onClick={onOuvrir} data-testid="cv-ouvrir">
                {t('dossier.ouvrirCv')}
              </button>
              <button type="button" className="btn-secondary btn-sm" onClick={() => printFrame(vignette.current?.contentWindow)}>
                {tLabels('preview.exportPdf')}
              </button>
            </span>
          </div>
        </div>
      ) : (
        <div className="atelier-pied atelier-pied-haut">
          <span className="when">{t('dossier.generatedOn', { date: fullDate(d.created_at) })}</span>
        </div>
      )}

      {(!enFenetre || ouvert) && <PreviewModal
        embedded={!enFenetre}
        t={tLabels}
        html={html}
        diffHtml={diffHtml}
        diffStats={d.diff.stats}
        fidelity={d.diff.fidelity}
        translation={traduction}
        docDemande={{ doc: 'letter', n: enFenetre ? (surLettre ? 1 : 0) : demandeLettre }}
        onDocumentReady={wireRevert}
        onSave={async (doc) => {
          const edited = harvestResume(doc, d.adapted)
          await post(`/api/dossiers/${d.id}/save`, { adapted: edited })
          toast(t('dossier.savedToast'))
          dossier.reload()
        }}
        onExportWord={() => { window.location.href = `/api/dossiers/${d.id}/export.docx` }}
        onRevise={async (selection, instruction) => {
          try {
            const r = await post<{ revised: string }>(`/api/dossiers/${d.id}/revise`, { selection, instruction })
            dossier.reload()
            return r.revised
          } catch (e: any) {
            toast(e.message, 'err')
            return ''
          }
        }}
        onClose={() => onFermer?.()}
        letterHtml={letterHtml}
        letterStale={d.letterStale}
        onKeepLetter={async () => {
          await post(`/api/dossiers/${d.id}/letter/keep`, {})
          dossier.reload()
        }}
        letterVersions={d.letterVersions.length > 1 ? d.letterVersions.map((v) => fullDate(v.at)) : undefined}
        letterVersionIndex={d.letterIndex}
        onSelectLetterVersion={
          d.letterVersions.length > 1
            ? async (i) => {
                await post(`/api/dossiers/${d.id}/letter/version`, { index: i })
                dossier.reload()
              }
            : undefined
        }
        onMakeDefault={async (choix) => {
          try {
            await post('/api/settings', { optimization: choix.optimization, tone: choix.tone, length: choix.length, language: choix.language })
            toast(t('dossier.defautEnregistre'))
          } catch (e: any) {
            toast(e.message, 'err')
          }
        }}
        // Un candidat, un dossier : changer de candidat remonte un atelier
        // neuf (key={consultantId} côté Offre.tsx), et la plupart ont déjà
        // une lettre en base. Sans ce réglage, cliquer un candidat après
        // l'autre ferait sauter l'écran sur la lettre à chaque fois au lieu
        // de rester sur le CV. Cf. le commentaire du prop dans PreviewModal.
        letterDefaultOnMount={false}
        generatingLetter={generatingLetter}
        onGenerateLetter={() => lettreAvecAngle()}
        onExportLetterWord={d.letter ? () => { window.location.href = `/api/dossiers/${d.id}/letter.docx` } : undefined}
        cvSettings={{ ...rendu, tone: d.settings.tone, length: d.settings.length, optimization: d.settings.optimization, language: d.settings.language }}
        letterSettings={{ ...rendu, tone: d.settings.tone, optimization: d.settings.optimization, language: d.settings.language }}
        onChangeRenderSettings={(partial) => {
          const suivant = { ...rendu, ...partial }
          setRendu(suivant)
          // En Radar, le modèle et la couleur s'enregistrent ensemble : la
          // couleur affichée peut n'exister qu'à l'écran (le noir de « Sobre »),
          // et le Word comme l'impression la relisent dans le dossier.
          const envoi = radar ? { ...partial, template: suivant.template, accentColor: suivant.accentColor } : partial
          post(`/api/dossiers/${d.id}/render-settings`, envoi).catch(() => {})
        }}
        onRegenerateWith={(partial, doc) => (doc === 'letter' ? lettreAvecAngle(partial) : regenererCv(partial))}
        onAskQuestions={(doc) => (doc === 'letter' ? lettreAvecAngle(undefined, true) : avecQuestions(() => regenererCv({})))}
        factsInUse={faits}
        unsupportedNumbers={chiffres}
        onRefineCv={(instruction) => regenererCv({}, instruction || undefined)}
        onRefineLetter={d.letter ? ajusterLettre : undefined}
        availableSections={availableSections}
        cvVersions={cvVersions.length > 1 ? cvVersions : undefined}
        cvVersionIndex={cvIndex >= 0 ? cvIndex : undefined}
        onSelectCvVersion={
          cvVersions.length > 1
            ? (i) => { const v = versions[i]; if (v) setDossierIdActuel(v.id) }
            : undefined
        }
        modeles={modeles}
        palette={radar ? PALETTE_RADAR : undefined}
        // Ma mise en page à préparer : elle se fait depuis Mon CV. Pas
        // ouverte par la formule : la page Abonnement, sans boîte de dialogue.
        onModeleVerrouille={() => { window.location.hash = miseEnPage.data?.utilisable ? '#/profil' : '#/abonnement' }}
        onModeleParDefaut={radar ? async () => {
          try {
            await post('/api/settings', { template: rendu.template, accentColor: rendu.accentColor })
            toast(t('modeles.parDefaut'))
          } catch (e: any) {
            toast(e.message, 'err')
          }
        } : undefined}
        wordStandard={perso}
      />}

      {/* Plus de bouton « Régénérer » ici : le bouton de la barre de consigne
          refait le CV, avec ou sans consigne (issue #178). Trois boutons qui
          faisaient la même chose ne laissaient pas savoir lequel choisir. */}
      {/* En fenêtre (Radar), le parcours porte déjà l'étape de candidature. */}
      {!enFenetre && <div className="atelier-pied">
        <div className="grow" />
        {/* L'avancement se règle sur la CANDIDATURE, jamais depuis le
            document : le dossier est une pièce jointe, il l'affiche et laisse
            la décision ailleurs (principe déjà en place avant la fusion). */}
        {candidature && (
          <SelecteurEtape
            candidatureId={candidature.id}
            status={candidature.status}
            onChanged={onChanged}
          />
        )}
      </div>}
    </div>
  )
}
